import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import type { TabType, ExerciseCatalogEntry, TrainingPlan, EditingTrainingPlan, PlanContext, Routine } from '../types';
import { MOCK_ROUTINES, MOCK_WORKOUTS, isDemoId } from '../data/mockData';

// Hooks personalizados
import { useExerciseCatalog } from '../hooks/useExerciseCatalog';
import { useRoutines } from '../hooks/useRoutines';
import { mergeQueuedWorkouts, useWorkouts, type SaveState } from '../hooks/useWorkouts';
import { useDataImportExport } from '../hooks/useDataImportExport';
import { useTrainingPlans } from '../hooks/useTrainingPlans';
import { saveDraft, saveDraftSync, loadDraft, clearDraft } from '../hooks/useWorkoutDraft';
import { useAppLifecycle } from '../hooks/useAppLifecycle';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useWorkoutQueue } from '../hooks/useWorkoutQueue';
import { OFFLINE_CACHE_KEYS } from '../utils/offlineCache';
import { removeStorageItem } from '../utils/storage';

interface DataContextType {
  routines: any[];
  workouts: any[];
  exerciseCatalog: any[];
  dataLoading: boolean;
  isDemoMode: boolean;
  isOffline: boolean;
  pendingCount: number;

  // Rutinas
  editingRoutine: any;
  setEditingRoutine: any;
  createRoutine: () => void;
  addExercise: () => void;
  addExerciseFromCatalog: (entry: { id: string; name: string }, sets?: number, reps?: number) => void;
  updateExercise: (index: number, field: string, value: any) => void;
  removeExercise: (index: number) => void;
  saveRoutine: () => Promise<void>;
  deleteRoutine: (id: string) => Promise<void>;
  ensureExerciseEntry: (name: string) => Promise<ExerciseCatalogEntry>;

  // Entrenamientos
  selectedRoutine: any;
  setSelectedRoutine: any;
  confirmedSets: Set<string>;
  setConfirmedSets: React.Dispatch<React.SetStateAction<Set<string>>>;
  activeExerciseIndex: number;
  setActiveExerciseIndex: React.Dispatch<React.SetStateAction<number>>;
  saveState: SaveState;
  startWorkout: (routine: any, planContext?: any) => void;
  updateWorkoutSet: (exIdx: number, setIdx: number, field: string, value: string) => void;
  addWorkoutSet: (exIdx: number) => void;
  removeWorkoutSet: (exIdx: number, setIdx: number) => void;
  saveWorkout: () => Promise<void>;
  deleteWorkout: (id: string, e: React.MouseEvent) => Promise<void>;
  editWorkout: (workout: any, e: React.MouseEvent, fromCalendar?: boolean) => void;
  toggleConfirmedSet: (exerciseIndex: number, setIndex: number) => void;
  cancelWorkout: () => void;

  // Planes de entrenamiento
  trainingPlans: TrainingPlan[];
  editingPlan: EditingTrainingPlan | null;
  setEditingPlan: React.Dispatch<React.SetStateAction<EditingTrainingPlan | null>>;
  createPlan: () => void;
  savePlan: () => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  activatePlan: (id: string) => Promise<void>;
  completePlan: (id: string) => Promise<void>;
  abandonPlan: (id: string) => Promise<void>;
  getActivePlan: () => TrainingPlan | undefined;
  getActivePlanContext: (routineId: string) => PlanContext | null;
  getUnplannedRoutines: (routines: Routine[]) => Routine[];

  // Gestión de datos
  loadData: () => Promise<void>;
  exportToJSON: () => void;
  exportToCSV: () => void;
  importFromJSON: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  clearAllData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
  setActiveTab: (tab: TabType) => void;
  setShowSettings: (show: boolean) => void;
  setSelectedWorkoutDetail: (workout: any) => void;
}

export function DataProvider({ children, setActiveTab, setShowSettings, setSelectedWorkoutDetail }: DataProviderProps) {
  const { user } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [isOffline, setIsOffline] = React.useState(false);
  const { queue, pendingCount, enqueue, syncQueue, clearQueue } = useWorkoutQueue(user?.id ?? null);

  // 1. Catálogo
  const {
    exerciseCatalog,
    catalogLoading,
    loadCatalog,
    ensureExerciseEntry,
    setExerciseCatalog
  } = useExerciseCatalog(user);

  // 2. Rutinas
  const {
    routines,
    setRoutines,
    routinesLoading,
    editingRoutine,
    setEditingRoutine,
    loadRoutines,
    createRoutine,
    addExercise,
    addExerciseFromCatalog,
    updateExercise,
    removeExercise,
    saveRoutine,
    deleteRoutine,
  } = useRoutines(user, showNotification, showConfirm, ensureExerciseEntry);

  // 3. Entrenamientos
  const {
    workouts,
    setWorkouts,
    workoutsLoading,
    selectedRoutine,
    setSelectedRoutine,
    confirmedSets,
    setConfirmedSets,
    activeExerciseIndex,
    setActiveExerciseIndex,
    saveState,
    loadWorkouts,
    persistQueuedWorkout,
    startWorkout,
    updateWorkoutSet,
    addWorkoutSet,
    removeWorkoutSet,
    saveWorkout,
    deleteWorkout,
    editWorkout,
    toggleConfirmedSet,
    cancelWorkout,
  } = useWorkouts(user, showNotification, showConfirm, ensureExerciseEntry, setActiveTab, setSelectedWorkoutDetail, enqueue, queue);

  // 4. Planes de entrenamiento
  const {
    trainingPlans,
    setTrainingPlans,
    plansLoading,
    editingPlan,
    setEditingPlan,
    loadTrainingPlans,
    createPlan,
    savePlan,
    deletePlan,
    activatePlan,
    completePlan,
    abandonPlan,
    getActivePlan,
    getActivePlanContext,
    getUnplannedRoutines,
  } = useTrainingPlans(user, showNotification, showConfirm);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      await Promise.all([loadCatalog(), loadRoutines(), loadWorkouts(), loadTrainingPlans()]);
    } finally {
      setIsInitialLoad(false);
    }
  }, [user, loadCatalog, loadRoutines, loadWorkouts, loadTrainingPlans]);

  const handleReconnect = useCallback(async () => {
    if (!user) return;
    await syncQueue(persistQueuedWorkout);
    await loadData();
  }, [user, syncQueue, persistQueuedWorkout, loadData]);

  const { isOnline } = useNetworkStatus(handleReconnect);

  React.useEffect(() => {
    setIsOffline(!isOnline);
  }, [isOnline]);

  React.useEffect(() => {
    if (!user || !isOnline || queue.length === 0) return;

    void handleReconnect();
  }, [user, isOnline, queue.length, handleReconnect]);

  // --- Hydrate plan days with routine objects ---
  const hydratedTrainingPlans = React.useMemo(() => {
    if (routines.length === 0 || trainingPlans.length === 0) return trainingPlans;
    return trainingPlans.map(plan => ({
      ...plan,
      days: plan.days.map(day => ({
        ...day,
        routine: routines.find(r => r.id === day.routine_id),
      })),
    }));
  }, [routines, trainingPlans]);

  const visibleWorkouts = React.useMemo(() => mergeQueuedWorkouts(workouts, queue), [workouts, queue]);

  // --- Draft auto-save (debounced 500ms) ---
  const draftTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (!user || !selectedRoutine || selectedRoutine.isDemo) return;

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraft(user.id, selectedRoutine, confirmedSets, activeExerciseIndex);
    }, 500);

    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [user, selectedRoutine, confirmedSets, activeExerciseIndex]);

  // Clear draft on successful save or cancel
  const wrappedSaveWorkout = useCallback(async () => {
    await saveWorkout(loadData);
    // If workout completed fully, selectedRoutine becomes null -> draft cleared below
  }, [saveWorkout, loadData]);

  const wrappedCancelWorkout = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    cancelWorkout();
    if (user) clearDraft(user.id);
  }, [cancelWorkout, user]);

  // --- App lifecycle: save draft when app goes to background ---
  const handlePause = useCallback(() => {
    if (user && selectedRoutine && !selectedRoutine.isDemo) {
      saveDraft(user.id, selectedRoutine, confirmedSets, activeExerciseIndex);
    }
  }, [user, selectedRoutine, confirmedSets, activeExerciseIndex]);

  const handlePauseSync = useCallback(() => {
    if (user && selectedRoutine && !selectedRoutine.isDemo) {
      saveDraftSync(user.id, selectedRoutine, confirmedSets, activeExerciseIndex);
    }
  }, [user, selectedRoutine, confirmedSets, activeExerciseIndex]);

  useAppLifecycle(handlePause, handlePauseSync);

  // --- Draft restoration on initial load ---
  const draftRestoredRef = React.useRef(false);
  const currentUserIdRef = React.useRef<string | null>(null);
  currentUserIdRef.current = user?.id ?? null;

  React.useEffect(() => {
    if (!user || isInitialLoad || draftRestoredRef.current) return;
    draftRestoredRef.current = true;

    const userId = user.id;

    loadDraft(userId).then(draft => {
      if (currentUserIdRef.current !== userId || !draft) return;
      showConfirm(
        'Hay un entrenamiento sin terminar. ¿Continuar? (Cancelar lo descarta)',
        () => {
          if (currentUserIdRef.current !== userId) return;
          setSelectedRoutine(draft.selectedRoutine);
          setConfirmedSets(new Set(draft.confirmedSets));
          setActiveExerciseIndex(draft.activeExerciseIndex);
          setActiveTab('workout');
        },
        () => {
          clearDraft(userId);
        }
      );
    }).catch(() => {
      if (currentUserIdRef.current === userId) clearDraft(userId);
    });
  }, [user, isInitialLoad, showConfirm, setSelectedRoutine, setConfirmedSets, setActiveExerciseIndex, setActiveTab]);

  // Trigger loadData when user is available or clear state on logout
  React.useEffect(() => {
    if (user) {
      setIsInitialLoad(true);
      loadData();
    } else {
      setRoutines([]);
      setWorkouts([]);
      setExerciseCatalog([]);
      setTrainingPlans([]);
      setEditingRoutine(null);
      setEditingPlan(null);
      setSelectedRoutine(null);
      setIsInitialLoad(false);
      setIsOffline(false);
      draftRestoredRef.current = false;
    }
  }, [user, loadData, setRoutines, setWorkouts, setExerciseCatalog, setEditingRoutine, setSelectedRoutine, setTrainingPlans, setEditingPlan]);

  // 4. Import/Export
  const { exportToJSON, exportToCSV, importFromJSON } = useDataImportExport(
    user, routines, workouts.filter(workout => !workout.pending), trainingPlans, ensureExerciseEntry, loadData, showNotification, setShowSettings
  );

  const clearAllData = async () => {
    if (!user) return;
    showConfirm('¿Borrar TODOS los datos? Esta acción es irreversible.', async () => {
      try {
        await supabase.from('training_plans').delete().eq('user_id', user.id);
        await Promise.all([
          supabase.from('routines').delete().eq('user_id', user.id),
          supabase.from('workouts').delete().eq('user_id', user.id),
          supabase.from('exercise_catalog').delete().eq('owner_user_id', user.id),
          removeStorageItem(OFFLINE_CACHE_KEYS.catalog(user.id)),
          removeStorageItem(OFFLINE_CACHE_KEYS.routines(user.id)),
          removeStorageItem(OFFLINE_CACHE_KEYS.workouts(user.id)),
          removeStorageItem(OFFLINE_CACHE_KEYS.plans(user.id)),
          removeStorageItem(OFFLINE_CACHE_KEYS.queue(user.id)),
          removeStorageItem(OFFLINE_CACHE_KEYS.queueLock(user.id)),
        ]);
        await clearQueue();
        setRoutines([]);
        setWorkouts([]);
        setTrainingPlans([]);
        showNotification('Datos borrados', 'success');
        setShowSettings(false);
      } catch (error) {
        showNotification('Error al borrar los datos', 'error');
      }
    });
  };

  const dataLoading = catalogLoading || routinesLoading || workoutsLoading || plansLoading || Boolean(user && isInitialLoad);
  const isDemoMode = routines.length === 0 && !dataLoading;

  return (
    <DataContext.Provider value={{
      routines: isDemoMode ? MOCK_ROUTINES : routines,
      workouts: isDemoMode ? MOCK_WORKOUTS : visibleWorkouts,
      exerciseCatalog,
      dataLoading,
      isDemoMode,
      isOffline,
      pendingCount,
      editingRoutine,
      setEditingRoutine,
      createRoutine,
      addExercise,
      addExerciseFromCatalog,
      updateExercise,
      removeExercise,
      saveRoutine: () => saveRoutine(loadData),
      deleteRoutine: (id) => deleteRoutine(id, isDemoId),
      ensureExerciseEntry,
      selectedRoutine,
      setSelectedRoutine,
      confirmedSets,
      setConfirmedSets,
      activeExerciseIndex,
      setActiveExerciseIndex,
      saveState,
      startWorkout,
      updateWorkoutSet,
      addWorkoutSet,
      removeWorkoutSet,
      saveWorkout: wrappedSaveWorkout,
      deleteWorkout: (id, e) => deleteWorkout(id, isDemoId, e),
      editWorkout,
      toggleConfirmedSet,
      cancelWorkout: wrappedCancelWorkout,
      trainingPlans: isDemoMode ? [] : hydratedTrainingPlans,
      editingPlan,
      setEditingPlan,
      createPlan,
      savePlan: () => savePlan(loadData),
      deletePlan,
      activatePlan: (id) => activatePlan(id, loadData),
      completePlan: (id) => completePlan(id, loadData),
      abandonPlan: (id) => abandonPlan(id, loadData),
      getActivePlan,
      getActivePlanContext,
      getUnplannedRoutines,
      loadData,
      exportToJSON,
      exportToCSV,
      importFromJSON,
      clearAllData,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) throw new Error('useData must be used within a DataProvider');
  return context;
}
